import React from "react";
import { useAppContext } from "../../AppProvider";
import { ButtonUtility } from "../../Components/common/Buttons/ButtonUtility";
import {
  useCollectionIndexContext,
  useCollectionBookContext,
} from "./providers";
import ButtonConfirm from "../../Components/Buttons/Confirm";

/* ===========================================================================
 * COMPONENT: CollectionBook
 * =========================================================================== */

/**
 * @component CollectionBook
 * * A confirmation modal content component for booking collections.
 * It leverages the `CollectionIndexContext` for UI configuration (like titles)
 * and the `CollectionBookContext` to execute the batch booking logic.
 *
 * @returns {JSX.Element} A UI fragment with confirmation message and action buttons.
 */
const CollectionBook: React.FC = () => {
  /** Access global dictionary for localized strings. */
  const { dictionary } = useAppContext();

  /** Access index-level modal settings. */
  const { modalConfig } = useCollectionIndexContext();

  /** * Access specific booking state and functions.
   * - data: The list of IDs to be booked.
   * - callback: Function to refresh the UI after action.
   * - loader: Boolean for async operation state.
   * - bookCollections: The execution trigger.
   */
  const { data, callback, loader, bookCollections } =
    useCollectionBookContext();

  /* ---------------------------------------------------------------------------
   * RENDER
   * ------------------------------------------------------------------------- */

  return (
    <React.Fragment>
      {/* Message Section */}
      <div className="row mb-3">
        <div className="col">
          <h3 className="text-center">
            {modalConfig.title || dictionary.actions.book}
          </h3>
          <label className="label-5 text-center">
            {/* Dynamic message based on single vs multiple selection */}
            {data?.length && data.length > 1
              ? dictionary.messages.ask_to_confirm_selected
              : dictionary.messages.ask_to_confirm}
          </label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="d-flex">
        {/* Cancel Button: Triggers the callback without booking */}
        <ButtonUtility
          className="w-50 me-2"
          onClick={() => {
            if (callback) {
              callback();
            }
          }}
        >
          {dictionary.actions.cancel}
        </ButtonUtility>

        {/* Confirm Button: Triggers the batch booking operation */}
        <ButtonConfirm
          className="w-50"
          onClick={() => bookCollections()}
          disabled={loader}
          loading={loader}
        >
          {dictionary.actions.book}
        </ButtonConfirm>
      </div>
    </React.Fragment>
  );
};

export default CollectionBook;
